// src/hooks/useJobs.js
import { useState, useCallback, useRef } from "react";
import { fetchCombinedJobs, filterJobs } from "../services/jobService";

const PAGE_SIZE = 20;

export default function useJobs() {
  const [jobs,    setJobs]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [page,    setPage]    = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [lastQuery, setLastQuery] = useState({
    keywords:    "",
    location:    "South Africa",
    keywordList: [],
    typeMode:    "all",
  });

  const abortControllerRef = useRef(null);

  const search = useCallback(async ({
    keywords    = "",
    location    = "South Africa",
    keywordList = [],
    typeMode    = "all",
  } = {}) => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);
    setPage(1);
    setLastQuery({ keywords, location, keywordList, typeMode });

    try {
      const raw = await fetchCombinedJobs({
        keywords,
        location,
        typeMode,
        page: 1,
        signal: controller.signal,
      });
      const result = keywordList.length > 0 ? filterJobs(raw, keywordList) : raw;
      setJobs(result);
      setHasMore(result.length >= PAGE_SIZE);
    } catch (err) {
      if (err.name !== "AbortError") {
        setError("Failed to fetch jobs. Please try again.");
        console.error("useJobs search error:", err);
      }
    } finally {
      setLoading(false);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    const nextPage = page + 1;

    try {
      const raw = await fetchCombinedJobs({
        ...lastQuery,
        page: nextPage,
        signal: controller.signal,
      });
      const result = lastQuery.keywordList.length > 0
        ? filterJobs(raw, lastQuery.keywordList)
        : raw;
      setJobs(prev => {
        const existingIds = new Set(prev.map(j => j.id));
        const fresh = result.filter(j => !existingIds.has(j.id));
        return [...prev, ...fresh];
      });
      setPage(nextPage);
      setHasMore(result.length >= PAGE_SIZE);
    } catch (err) {
      if (err.name !== "AbortError") {
        setError("Failed to load more jobs.");
      }
    } finally {
      setLoading(false);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [loading, hasMore, page, lastQuery]);

  return { jobs, loading, error, search, loadMore, hasMore };
}